import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { IAddressPrediction, IGetAddressPredictionsParams, IPickUpKeyAddress } from './models';
import { AWS_ADDRESS_ENDPOINT, datahubAxiosClient, ENDPOINT_DETAILS } from 'constants/server/apiEndPoint';
import { DEFAULT_TABLE_PAGE_NUM, DEFAULT_TABLE_PAGE_SIZE } from 'constants/ui/style';
import { mapAddressPredictionFromDawaDto, mapAddressPredictionFromGoogleVersion2 } from './helpers';
import { useAuth } from 'modules/auth/hooks';
import { loadGoogleMapsScriptConfig } from 'config/loadGoogleMapsScriptConfig';
import { MAX_RETRY_ATTEMPTS } from 'constants/server/apiSettings';

type GetAddressPayload = {
  addressTextSearch: string;
  branchCountryCode?: string;
};

type KeyPickUpAddress = {
  items: IPickUpKeyAddress[];
  totalRecords: number;
};

export const addressQueryKey = {
  list: (filters: string) => ['address', 'list', { filters }] as const,
};

// Address handler for managing different APIs (Dawa or Google)
export const addressHandler = {
  getDawaAddressPredictions: async ({ query }: IGetAddressPredictionsParams) => {
    const client = axios.create({
      baseURL: import.meta.env.VITE_DAWA_ADDRESS_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, //30s
    });
    const response = await client.get(AWS_ADDRESS_ENDPOINT, {
      params: {
        q: query,
        side: DEFAULT_TABLE_PAGE_NUM,
        per_side: DEFAULT_TABLE_PAGE_SIZE,
      },
    });

    return response?.data?.map?.(mapAddressPredictionFromDawaDto) || [];
  },
  getGoogleAddressPredictions: async ({
    query,
    countryCode,
  }: IGetAddressPredictionsParams): Promise<IAddressPrediction[]> => {
    await loadGoogleMapsScriptConfig();

    const service = new google.maps.places.AutocompleteService();

    const data = await service.getPlacePredictions({
      input: query,
      componentRestrictions: {
        country: countryCode,
      },
      types: ['address'],
    });
    if (!data || !data.predictions) {
      return [];
    }
    const transformData = await Promise.all(
      data.predictions.map(async address => {
        const details = await addressHandler.getPlaceDetailsById(address.place_id);
        const fullAddress = mapAddressPredictionFromGoogleVersion2(details);
        return fullAddress;
      })
    );

    const response = transformData.filter(Boolean) as IAddressPrediction[];
    return response;
  },
  getAddressPredictions: async (params: IGetAddressPredictionsParams) => {
    const { countryCode } = params;
    return countryCode === 'dk'
      ? addressHandler.getDawaAddressPredictions(params)
      : addressHandler.getGoogleAddressPredictions(params);
  },
  getPlaceDetailsById: async (placeId: string): Promise<google.maps.places.PlaceResult> => {
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    return new Promise((resolve, reject) => {
      service.getDetails(
        {
          placeId: placeId,
          fields: ['address_component', 'formatted_address', 'geometry', 'name'],
        },
        (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            resolve(result);
          } else {
            console.error('Place Details API failed:', status);
            reject(new Error(`Place Details API failed with status: ${status}`));
          }
        }
      );
    });
  },
};

export const getAddressSuggestionsApi = (props: GetAddressPayload) => {
  const { addressTextSearch, branchCountryCode } = props;
  const { user } = useAuth();
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: addressQueryKey.list(addressTextSearch),
    queryFn: async () => {
      const response = await addressHandler.getAddressPredictions({
        query: addressTextSearch,
        countryCode: (branchCountryCode || user?.branches?.[0]?.countryCode)?.toLowerCase() ?? '',
      });
      return response;
    },
  });

  return { data, isLoading, isSuccess };
};

export const useGetPickUpKeyAddressApi = (branchId: string, isPickUpKeyChosen: boolean) => {
  const { data, isLoading, isSuccess } = useQuery<KeyPickUpAddress>({
    queryKey: addressQueryKey.list(branchId),
    queryFn: async () => {
      const response = await datahubAxiosClient.get(ENDPOINT_DETAILS.getAddresses(branchId), {
        params: {
          limit: DEFAULT_TABLE_PAGE_SIZE,
          page: DEFAULT_TABLE_PAGE_NUM,
        },
      });
      return response.data;
    },
    enabled: !!branchId && isPickUpKeyChosen,
    retry: MAX_RETRY_ATTEMPTS,
  });

  return { data, isLoading, isSuccess };
};
